import { useTranslation } from "react-i18next";
import i18n from "i18next";
const t = (key: string, fallback?: string) => i18n.t(key, fallback || key);
import {
	DownOutlined, PlusOutlined, SearchOutlined
} from '@ant-design/icons';
import {
	Button, Dropdown,
	Form, Layout, message, Typography
} from 'antd';
import type { MenuProps } from 'antd/lib';
import React, { ChangeEvent, KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SmartBreadcrumb, { BreadcrumbItem } from '../../../components/breadcrumb/SmartBeadcrumb';
import FormInput from '../../../components/form-input/FormInput';
import AddLessionModal from '../../../components/modal/add-lession';
import PageHeader from '../../../components/page-header/PageHeader';
import { routesConfig } from '../../../config/routes';
import useKeyword from '../../../hooks/useKeyword';
import { createLearningFolder, updateFolder } from '../../../service/learning-material';
import { getAllTeacherAndFacility } from '../../../service/library/question-list';
import { LearningMaterialStore, useLearningRepositoryStore } from '../../../stores/stores';
import { LearningMaterialV2Type } from '../../../types/learning-material';
import { AuthorizedUserType } from '../../../types/listTrainingNeeds';
import { notifyError } from '../../../utils/notification';
import { sharePermissionType } from '../../../utils/sharePermission';
import CreateFolderDrawer from './components/create-folder-drawer/CreateFolderDrawer';
import HistoryUseLearningModal from './components/history-modal/HistoryUseLearningModal';
import LearningMaterialView from './components/learning-material-view/LearningMaterialView';
import LearningMaterialDrawer from './components/LearningMaterialDrawer/LearningMaterialDrawer';
import "./style.scss";


const LearningMaterials: React.FC<{ title?: string }> = ({ title }) => {
  const { t } = useTranslation();
	const navigate = useNavigate();
	const { folderId } = useParams();
	const { onReload } = LearningMaterialStore();
	const { folderInfo, set } = useLearningRepositoryStore((s) => ({
		keyword: s.keyword,
		set: s.set,
		folderInfo: s.folderInfo
	}));

	const [showFilter, setShowFilter] = useState(false);
	const [addLessonOpen, setAddLessonOpen] = useState(false);
	const [drawerState, setDrawerState] =
		useState<null | { type: 'lesson' | 'folder', id?: number, module?: any, edit?: boolean }>(null);
	const [historyId, setHistoryId] = useState<number | null>(null);
	const [searchValue, setSearchValue] = useState<string>('');
	const dropdownItems: MenuProps['items'] = [
		{ key: 'folder', label: t('Thêm thư mục', 'Thêm thư mục'), onClick: () => setDrawerState({ type: 'folder' }) },
		{ key: 'lesson', label: t('Thêm học liệu', 'Thêm học liệu'), onClick: () => setAddLessonOpen(true) },
	];
	const searchParams = new URLSearchParams(location.search);


	const buildBreadcrumbItems = (): BreadcrumbItem[] => {
		const items: BreadcrumbItem[] =
			folderInfo?.parentFolders?.map(f => ({ id: f.id, name: f.name, isMine: f.isMine })) || [];

		if (
			folderId &&
			folderInfo?.name &&
			items.at(-1)?.id !== folderInfo.id
		) {
			items.push({ id: folderInfo.id, name: folderInfo.name, isMine: folderInfo.isMine });
		}
		return items;
	};

	const isMineFolder = useKeyword("isMine")
	const isSharedFolder = useKeyword("isShared")

	useEffect(() => {
		if (isMineFolder) {
			set("tab", "1");
		} else if (isSharedFolder) {
			set("tab", "2");
		}
		else set("tab", "1");

	}, []);


	const renderHeaderTitle = () => title

		? title
		: <SmartBreadcrumb
			basePath={`/${routesConfig.courseLibrary}/${routesConfig.learningRepository}`}
			rootPath={`/${routesConfig.courseLibrary}/${routesConfig.learningRepository}`}
			items={buildBreadcrumbItems()}
			preserveSearch
			onItemClick={(item) => {
				const newPath = item.id === 'root'
					? `/${routesConfig.courseLibrary}/${routesConfig.learningRepository}`
					: `/${routesConfig.courseLibrary}/${routesConfig.learningRepository}/${item.id}`; if (item.id === 'root') {
						searchParams.delete("isMine");
						searchParams.delete("isShared");
						set("tab", "1");
					}

				else {

					if (item.isMine) {
						searchParams.set("isMine", "true");
						searchParams.delete("isShared");
						set("tab", "1");
					} else {
						searchParams.set("isShared", "true");
						set("tab", "2");
						searchParams.delete("isMine");
					}
				}
				navigate({
					pathname: newPath,
					search: searchParams.toString(),
				});
			}}
		/>

	useEffect(() => {
		(async () => {
			const res = await getAllTeacherAndFacility({});
			const uniq = new Map();
			res?.data?.data?.content?.forEach((u: AuthorizedUserType) =>
				u.email && !uniq.has(u.email) && uniq.set(u.email, u));
		})();
	}, []);



	const handleEdit = useCallback((rec: LearningMaterialV2Type) => {
		if (rec.isUnit) {
			setDrawerState({
				type: 'lesson',
				id: rec.units?.[0]?.id,
				module: rec.unitModule,
				edit: true,
			});
		} else {
			setDrawerState({ type: 'folder', id: rec.id });
		}
	}, []);


	const handleShare = (rec: LearningMaterialV2Type) => {
		navigate(`/${routesConfig.courseLibrary}/${routesConfig.sharePermission}/${rec.id}?type=${sharePermissionType.UNIT_RESOURCE}`)
	}

	const handleChangeMenuType = (type: any) => {
		setDrawerState({ type: 'lesson', module: type });
		setAddLessonOpen(false);
	};

	const confirmAddLesson = () => setAddLessonOpen(false);

	const handleAddFolder = async (values: any) => {
		const payload = {
			name: values.name,
			parentId: folderId
		};
		try {
			await createLearningFolder(payload);
			message.success(t('Thêm thư mục thành công', 'Thêm thư mục thành công'));
			setDrawerState(null);
			onReload();
		} catch {
			notifyError(t('Lỗi khi lưu thư mục', 'Lỗi khi lưu thư mục'));
		}
	}
	const handleUpdateFolder = async (values: any) => {
		const payload = {
			name: values.name,
		};
		try {
			await updateFolder(Number(drawerState?.id), payload);
			message.success(t('Sửa thư mục thành công', 'Sửa thư mục thành công'));
			setDrawerState(null);
			onReload();
		} catch {
			notifyError(t('Lỗi khi lưu thư mục', 'Lỗi khi lưu thư mục'));
		}
	}

	const handleChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
		setSearchValue(e.target.value);
	};

	const handleSubmitSearch = () => {
		set("keyword", searchValue);
		set("page", 1);
	};

	const handleSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleSubmitSearch();
	};

	return (
		<Layout className="page-header-group learning-repository">
			<Form.Provider>
				<div className="page-header-group--layout">
					<div className="table-header--wrapper justify-between">
						<PageHeader title={renderHeaderTitle()} />
						<div className="flex gap-8">

							<Button className="filter-button" onClick={() => setShowFilter((s) => !s)}>
								<Typography.Text>{t("Bộ lọc", "Bộ lọc")}</Typography.Text>
								<DownOutlined />
							</Button>
							<FormInput
								placeholder={t("Nhập tên để tìm kiếm", "Nhập tên để tìm kiếm")}
								value={searchValue}
								onChange={handleChangeInput}
								onKeyPress={handleSearchKey}
								afterPrefixIcon={<SearchOutlined onClick={handleSubmitSearch} />}
								style={{ width: 400 }}
							/>
							{folderInfo?.isCanEdit || folderInfo === null ?
								<Dropdown menu={{ items: dropdownItems }} trigger={["click"]}>
									<Button type="primary">
										{t("Thêm", "Thêm")} <PlusOutlined />
									</Button>
								</Dropdown> : null}
						</div>
					</div>

				</div>
				<LearningMaterialView
					folderId={folderId}
					showFilter={showFilter}
					onCloseFilter={() => setShowFilter(false)}
					onEdit={handleEdit}
					onShowHistory={(hId) => setHistoryId(hId)}
					onSharePermission={handleShare}
					enableMultiDelete={true}
				/>
			</Form.Provider>


			<AddLessionModal
				open={addLessonOpen}
				title={t("Chọn học liệu", "Chọn học liệu")}
				closeIcon={true}
				okText={t("Xác nhận", "Xác nhận")}
				cancelText={t("Huỷ", "Huỷ")}
				icon={null}
				confirm={confirmAddLesson}
				multimediaType={handleChangeMenuType}
				closeModal={() => setAddLessonOpen(false)}
				isCheckDocument={false}
			/>

			{drawerState?.type === 'lesson' && (
				<LearningMaterialDrawer
					open
					multimediaType={drawerState.module ?? ''}
					learningMaterialId={drawerState.id}
					isEdit={drawerState.edit}
					onCloseDraw={() => setDrawerState(null)}
				/>
			)}


			{drawerState?.type === 'folder' && (
				<CreateFolderDrawer
					open
					handleClose={() => setDrawerState(null)}
					onSubmit={(values) => {
						!drawerState?.id ? handleAddFolder(values) : handleUpdateFolder(values)
					}}
					folderId={drawerState.id}
				/>
			)}

			{historyId && (
				<HistoryUseLearningModal
					open
					historyId={historyId}
					handleCancel={() => setHistoryId(null)}
					handleOk={() => setHistoryId(null)}
				/>
			)}
		</Layout>
	);
};

export default LearningMaterials;
